// src/components/wallet/AUSDRewards.js
import React, { useEffect, useState, useCallback } from 'react';
import Web3 from 'web3';
import ausdDistributorAbi from '../../contracts/ausdDistributorAbi';
import { formatCurrencyExtended } from '../../utils';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Progress } from '@nextui-org/react';

const AUSD_DECIMALS = 1e18;      // 18-dec coin

const AUSDRewards = React.memo(
  ({ account, contracts, viewTotalDistributed = false, enableClaiming = false }) => {
    const [distributor,      setDistributor]      = useState(null);
    const [unclaimed,        setUnclaimed]        = useState(0);
    const [totalRealised,    setTotalRealised]    = useState(0);
    const [totalDistributed, setTotalDistributed] = useState(0);
    const [isClaiming,       setIsClaiming]       = useState(false);

    /* ────────── fetch the distributor contract once ────────── */
    useEffect(() => {
      let isMounted = true;

      const initDistributor = async () => {
        if (!contracts?.ausd) return;
        const web3 = new Web3(window.ethereum);
        const addr = await contracts.ausd.methods.getDistributor().call();
        if (isMounted) setDistributor(new web3.eth.Contract(ausdDistributorAbi, addr));
      };

      initDistributor();
      return () => { isMounted = false; };
    }, [contracts?.ausd]);

    /* centralised fetch so we can reuse after claim / on poll */
    const fetchRewards = useCallback(async () => {
      if (!distributor || !account) return;

      const [uc, dist, shares] = await Promise.all([
        distributor.methods.getUnclaimedEarnings(account).call(),
        distributor.methods.totalDistributed().call(),
        distributor.methods.shares(account).call()
      ]);

      setUnclaimed(uc);
      setTotalDistributed(dist);
      setTotalRealised(shares.totalRealised);
    }, [distributor, account]);

    /* initial load + polling every 60 s */
    useEffect(() => {
      if (!distributor) return;
      fetchRewards();                       // first run
      const id = setInterval(fetchRewards, 60_000);
      return () => clearInterval(id);
    }, [distributor, fetchRewards]);

    /* ───────────── claim handler ───────────── */
    const handleClaim = async () => {
      if (!enableClaiming || isClaiming) return;
      setIsClaiming(true);

      try {
        await distributor.methods.claimDividend().send({ from: account });
        await fetchRewards();               // self-refresh
      } catch (err) {
        console.error('AUSD claim error:', err);
      } finally {
        setIsClaiming(false);
      }
    };

    const fmt = v => formatCurrencyExtended(v / AUSD_DECIMALS);

    return (
      <div style={{
        borderRadius: 10,
        background   : 'rgba(50,100,255,0.05)',
        padding      : 5,
        fontSize     : 11
      }}>
        <div>Reward:&nbsp;<strong>AUSD</strong></div>

        {viewTotalDistributed && (
          <div>
            Total&nbsp;Distributed:&nbsp;
            <strong>
              {totalDistributed
                ? fmt(totalDistributed)
                : <Skeleton width="100%" height="12px" className="mb-2" />}
            </strong>
          </div>
        )}

        {+totalRealised === 0 && +unclaimed === 0 && (
          <div><strong>No Rewards Pending</strong></div>
        )}

        {totalRealised > 0 && (
          <div>
            Earned:&nbsp;
            <strong>{fmt(totalRealised)}</strong>
          </div>
        )}

        {unclaimed > 0 && (
          <div>
            Pending:&nbsp;
            <strong>{fmt(unclaimed)}</strong>
          </div>
        )}

        {unclaimed > 0 && enableClaiming && (
          isClaiming ? (
            <Progress indeterminate style={{ marginTop: 10 }} />
          ) : (
            <Button
              rounded
              severity="warning"
              size="small"
              disabled={isClaiming}
              onClick={handleClaim}
              style={{ marginTop: 10, width: 200, justifyContent: 'center' }}
            >
              Claim
            </Button>
          )
        )}
      </div>
    );
  }
);

export default AUSDRewards;
